import React, {useState, useRef, useEffect} from 'react';
import {logout} from "@app/utils/utils";
import { useNavigate } from 'react-router-dom';
import { CaretDownIcon, UserIcon, CogIcon, SignOutAltIcon } from '@patternfly/react-icons';
import { MyProfileModal } from './MyProfileModal';

export interface IUsernameDropdownComponentProps {
    username: string
}

const UsernameDropdown : React.FunctionComponent<IUsernameDropdownComponentProps> = (props) => {

    const [isOpen, setIsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    // Get the first letter of the username, uppercase
    const getInitial = () => {
        if (props.username && props.username.length > 0) {
            return props.username.charAt(0).toUpperCase();
        }
        return '?';
    };

    // Get the user role display text based on roles from localStorage
    const getRoleDisplay = () => {
        try {
            const rolesString = localStorage.getItem('roles');
            if (rolesString) {
                const parsed = JSON.parse(rolesString);
                if (Array.isArray(parsed)) {
                    // The API may return roles as a single comma-joined string,
                    // e.g. ["ROLE_USER,ROLE_ADMIN"] — normalize to individual roles
                    const roles = parsed.flatMap((r) => String(r).split(',')).map((r) => r.trim());
                    if (roles.includes('ROLE_USER') && roles.includes('ROLE_ADMIN')) {
                        return 'Owner++';
                    }
                    if (roles.includes('ROLE_USER')) {
                        return 'Owner';
                    }
                }
            }
        } catch (e) {
            // If parsing fails, show no role label
        }
        return '';
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    const handleLogout = () => {
        setIsOpen(false);
        logout();
    };

    const handleOpenProfile = () => {
        setIsOpen(false);
        setShowProfileModal(true);
    };

    const handleOpenSettings = () => {
        setIsOpen(false);
        navigate('/settings');
    };

    // Styles
    const styles = {
        container: {
            position: 'relative' as const
        },
        toggleButton: {
            background: isHovered ? '#f3f4f6' : 'transparent',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '8px 12px',
            borderRadius: '12px',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
        },
        avatar: {
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            fontSize: '14px',
            fontWeight: 700,
            letterSpacing: '0.5px',
            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
        },
        userInfo: {
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'flex-start'
        },
        username: {
            color: '#1f2937',
            fontSize: '14px',
            fontWeight: 600
        },
        userRole: {
            color: '#9ca3af',
            fontSize: '11px',
            fontWeight: 500
        },
        caretIcon: {
            color: '#9ca3af',
            fontSize: '12px',
            transition: 'transform 0.2s',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
        },
        dropdownMenu: {
            position: 'absolute' as const,
            top: '100%',
            right: 0,
            marginTop: '8px',
            background: '#ffffff',
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            minWidth: '220px',
            zIndex: 1000,
            border: '1px solid #e5e7eb'
        },
        menuHeader: {
            padding: '20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            gap: '14px'
        },
        menuHeaderAvatar: {
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            fontSize: '20px',
            fontWeight: 700
        },
        menuHeaderInfo: {
            flex: 1
        },
        menuHeaderName: {
            color: '#ffffff',
            fontSize: '16px',
            fontWeight: 600,
            margin: 0
        },
        menuHeaderStatus: {
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '12px',
            margin: '4px 0 0 0',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
        },
        onlineDot: {
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: '#34d399'
        },
        menuItems: {
            padding: '8px'
        },
        menuItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            color: '#374151',
            cursor: 'pointer',
            transition: 'all 0.2s',
            border: 'none',
            background: 'none',
            width: '100%',
            textAlign: 'left' as const,
            borderRadius: '10px'
        },
        menuItemIcon: {
            color: '#667eea',
            fontSize: '18px',
            width: '20px'
        },
        menuDivider: {
            height: '1px',
            background: '#e5e7eb',
            margin: '8px'
        },
        logoutItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            color: '#dc2626',
            cursor: 'pointer',
            transition: 'all 0.2s',
            border: 'none',
            background: 'none',
            width: '100%',
            textAlign: 'left' as const,
            borderRadius: '10px'
        },
        logoutIcon: {
            color: '#dc2626',
            fontSize: '18px',
            width: '20px'
        }
    };

    return (
        <div style={styles.container} ref={dropdownRef}>
            <button
                type="button"
                onClick={handleToggle}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={styles.toggleButton}
            >
                <div style={styles.avatar}>
                    {getInitial()}
                </div>
                <div style={styles.userInfo}>
                    <span style={styles.username}>{props.username}</span>
                    {getRoleDisplay() && <span style={styles.userRole}>{getRoleDisplay()}</span>}
                </div>
                <CaretDownIcon style={styles.caretIcon} />
            </button>

            {isOpen && (
                <div style={styles.dropdownMenu}>
                    {/* Header with user info */}
                    <div style={styles.menuHeader}>
                        <div style={styles.menuHeaderAvatar}>
                            {getInitial()}
                        </div>
                        <div style={styles.menuHeaderInfo}>
                            <p style={styles.menuHeaderName}>{props.username}</p>
                            <p style={styles.menuHeaderStatus}>
                                <span style={styles.onlineDot} />
                                Online
                            </p>
                        </div>
                    </div>

                    {/* Menu Items */}
                    <div style={styles.menuItems}>
                        <button
                            style={styles.menuItem}
                            onClick={handleOpenProfile}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >
                            <UserIcon style={styles.menuItemIcon} />
                            My Profile
                        </button>
                        <button
                            style={styles.menuItem}
                            onClick={handleOpenSettings}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >
                            <CogIcon style={styles.menuItemIcon} />
                            Settings
                        </button>

                        <div style={styles.menuDivider} />

                        <button
                            style={styles.logoutItem}
                            onClick={handleLogout}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >
                            <SignOutAltIcon style={styles.logoutIcon} />
                            Sign Out
                        </button>
                    </div>
                </div>
            )}

            {/* My Profile Modal */}
            <MyProfileModal
                isOpen={showProfileModal}
                onClose={() => setShowProfileModal(false)}
                username={props.username}
            />
        </div>
    );
};
export {UsernameDropdown}
